import { useState } from 'react';
import { Stack } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { useDebounce } from '@uidotdev/usehooks';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { RelativelyPositionedSearchFilter, Table } from '@/components';
import { DEBOUNCE_TIME_MS } from '@/constants';
import {
  getSavedCurrentPage,
  getSavedRowsPerPage,
  handleActivateItems,
  handleDeactivateItems,
  handleDeleteItems,
  handleDuplicateItems,
  onSelectAll,
  onSelectRow,
} from '@/core';
import { DefaultCell, IsActiveStatusCell } from '@/core/renderCells';
import { IngredientsToolBar, PageHeader } from '@/features';
import { useIngredients } from '@/features/product/hooks';
import { INGREDIENTS_ROUTE } from '@/routes';

const INGREDIENTS_PAGE_KEY = 'ingredients';

export const PageIngredients = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [selectedRows, setSelectedRows] = useState<number[]>([]);

  const [currentPage, setCurrentPage] = useState<number>(getSavedCurrentPage(INGREDIENTS_PAGE_KEY));
  const [rowsPerPage, setRowsPerPage] = useState<number>(getSavedRowsPerPage(INGREDIENTS_PAGE_KEY));

  const [searchInputValue, setSearchInputValue] = useState<string>('');
  const debouncedSearchInputValue = useDebounce(searchInputValue, DEBOUNCE_TIME_MS);

  //queries
  const {
    mutations: { duplicateMutation, deleteMutation, activateMutation, deactivateMutation },
    queries: { useSearchIngredientByName, useIngredientsQuery },
  } = useIngredients();
  const { data, status, refetch } = useIngredientsQuery({
    limit: rowsPerPage,
    page: currentPage,
  });
  const { data: ingredientByName, status: ingredientByNameStatus } = useSearchIngredientByName({
    name: debouncedSearchInputValue,
  });

  const isFilterActive = debouncedSearchInputValue.length > 0;
  const displayRows = isFilterActive ? ingredientByName : data?.results || [];
  const displayTotal = isFilterActive ? ingredientByName?.length || 0 : data?.total_results || 0;
  const paginationTotal = isFilterActive
    ? ingredientByName?.length || (debouncedSearchInputValue ? 1 : 0)
    : data?.total_results || 0;

  const handleCancelAction = () => {
    setSelectedRows([]);
  };

  const handleDuplicateIngredients = async () => {
    await handleDuplicateItems({
      selectedRows,
      setSelectedRows,
      refetch,
      duplicateMutation,
    });
  };

  const handleDeleteIngredients = async () => {
    await handleDeleteItems({
      selectedRows,
      setSelectedRows,
      refetch,
      deleteMutation,
    });
  };

  const handleActivateIngredients = async () => {
    await handleActivateItems({
      selectedRows,
      setSelectedRows,
      refetch,
      activateMutation,
    });
  };

  const handleDeactivateIngredients = async () => {
    await handleDeactivateItems({
      selectedRows,
      setSelectedRows,
      refetch,
      deactivateMutation,
    });
  };

  const onCreateButtonClick = () => {
    navigate(`${INGREDIENTS_ROUTE}/create`);
  };

  const inEditRow = (rowId: number) => {
    navigate(`${INGREDIENTS_ROUTE}/${rowId}`);
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: t('HEADERS.NAME'),
      flex: 0.3,
      renderCell: (params) => <DefaultCell value={params.value} />,
    },
    {
      field: 'unit',
      flex: 0.2,
      headerName: t('HEADERS.UNIT'),
      renderCell: (params) => <DefaultCell value={params.value} />,
    },
    {
      field: 'is_active',
      flex: 1,
      headerName: t('HEADERS.STATUS'),
      renderCell: (params) => <IsActiveStatusCell status={params.value} />,
    },
  ];

  return (
    <Stack>
      <PageHeader onButtonClick={onCreateButtonClick} actionButtonLabel={t('INGREDIENT.ADD')} />
      <RelativelyPositionedSearchFilter
        inputValue={searchInputValue}
        setInputValue={setSearchInputValue}
      />
      <Table
        key={`${searchInputValue}_${currentPage}_${rowsPerPage}_${JSON.stringify(data)}_${JSON.stringify(ingredientByName)}${isFilterActive}`}
        rows={displayRows}
        tableId={INGREDIENTS_PAGE_KEY}
        columns={columns}
        isDraggable={false}
        isCheckBox={true}
        isRowsEditable={true}
        selectedRows={selectedRows}
        onSelectRow={(selectedRowId) => onSelectRow(selectedRowId, setSelectedRows)}
        onSelectAll={() => onSelectAll(data, selectedRows, setSelectedRows)}
        pagination
        paginationOptions={{
          currentPage,
          rowsPerPage,
          totalRows: paginationTotal,
          rowCount: paginationTotal,
          rowsPerPageOptions: [15, 25, 50, 100],
          onPageSizeChange: (newPageSize: number) => {
            setCurrentPage(0);
            setRowsPerPage(newPageSize);
          },
          onPageChange: (newPage: number) => {
            setCurrentPage(newPage);
          },
          pageSize: rowsPerPage,
          page: currentPage,
        }}
        onEditRow={inEditRow}
        loading={status === 'pending' || ingredientByNameStatus === 'pending'}
        ToolBar={(props) => (
          <IngredientsToolBar
            {...props}
            count={displayTotal}
            selectedRows={selectedRows}
            onActivate={handleActivateIngredients}
            onDeactivate={handleDeactivateIngredients}
            onDuplicate={handleDuplicateIngredients}
            onCancel={handleCancelAction}
            onDelete={handleDeleteIngredients}
          />
        )}
      />
    </Stack>
  );
};
